import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { SearchIcons } from "../../../../../core/global/Icons";
import Button from "../../../../../core/ui/Button";
import { rqx } from "../../../../../core/request/API";
import { env } from "../../../../../core/Env";
import { useDebounce } from "../../../../../core/hooks/useDebounce";

const EngagementSelectionModal = ({
  open,
  onClose,
  onConfirm,
  companyId,
  clientId,
  selectedEngagements = [],
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [checked, setChecked] = useState(selectedEngagements);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (!open || !companyId) return;
    let isMounted = true;
    setLoading(true);
    const qsp = {
      company_id: companyId,
      client_id: clientId
    };
    if (debouncedSearch.trim()) qsp.q = debouncedSearch.trim();
    rqx
      .get(`<<tool>>/${env()}/leads/engagement/read`, qsp)
      .then((result) => {
        if (isMounted) setEngagements(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        if (isMounted) setEngagements([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [open, companyId, clientId, debouncedSearch]);

  const isChecked = (engId) => checked.some((c) => c.id === engId);

  const handleToggle = (eng) => {
    setChecked((prev) =>
      prev.some((c) => c.id === eng.id)
        ? prev.filter((c) => c.id !== eng.id)
        : [...prev, eng],
    );
  };

  const handleConfirm = () => {
    onConfirm(checked);
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          padding: "24px",
          maxWidth: "600px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: "24px",
          color: "#292C2F",
          padding: 0,
          marginBottom: "24px",
        }}
      >
        Select Engagement
      </DialogTitle>
      <DialogContent
        sx={{ padding: 0, marginBottom: "24px", overflow: "hidden" }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search Engagement"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcons
                  sx={{ color: "#283745", width: "20px", height: "20px" }}
                />
              </InputAdornment>
            ),
            sx: {
              height: "40px",
              backgroundColor: "#F7F8F8",
              padding: "8px 12px",
              borderRadius: "6px",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "24px",
              "& fieldset": {
                border: "none",
              },
              "& input": {
                padding: 0,
                paddingLeft: "12px",
                color: "#292C2F",
              },
              "& input::placeholder": {
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "#8F969D",
                opacity: 1,
              },
            },
          }}
        />
        <Box sx={{ maxHeight: "350px", overflowY: "auto", mt: 3, pr: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} sx={{ color: "#003F2E" }} />
              </Box>
            ) : engagements.length > 0 ? (
              engagements.map((eng, idx) => (
                <Box
                  key={eng.id}
                  onClick={() => handleToggle(eng)}
                  sx={{
                    padding: "16px 0 16px 0",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    borderBottom:
                      idx !== engagements.length - 1
                        ? "1px solid #D6D9DB"
                        : "none",
                    transition: "background 0.2s ease",
                  }}
                >
                  <Checkbox
                    checked={isChecked(eng.id)}
                    onChange={() => handleToggle(eng)}
                    sx={{
                      padding: 0,
                      color: "#D6D9DB",
                      "&.Mui-checked": {
                        color: "#003F2E",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "20px",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "24px",
                      color: "#292C2F",
                    }}
                  >
                    {eng.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  sx={{
                    color: "#8F969D",
                    fontSize: "14px",
                    fontFamily: "Inter",
                  }}
                >
                  No engagements found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 0, gap: "12px" }}>
        <Button
          onClick={handleConfirm}
          disabled={checked.length === 0}
          variant="dark"
          sx={{
            height: "52px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            fontFamily: "Inter",
          }}
        >
          Select
        </Button>
        <Button
          onClick={handleClose}
          variant="neutral"
          sx={{
            height: "52px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            fontFamily: "Inter",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EngagementSelectionModal;
